import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Search, Download, RefreshCw, Users, CalendarClock, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bks_admin_token";

function AdminLogin({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back.");
      onSuccess(data.token);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6">
      <div className="form-card p-8 w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#3df3c2]/10 border border-[#3df3c2]/25 flex items-center justify-center">
            <Lock className="w-5 h-5 text-[#3df3c2]" />
          </div>
        </div>
        <h1 className="text-white text-xl font-semibold text-center">Admin Access</h1>
        <p className="text-white/50 text-sm text-center mt-2">Enter your password to view leads.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="input-dark w-full px-4 py-3 rounded-xl text-sm"
          />
          <button
            type="submit"
            disabled={loading}
            className="btn-mint w-full py-3 rounded-xl text-sm font-semibold disabled:opacity-70"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ Icon, label, value }) {
  return (
    <div className="card-dark p-5 flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-[#3df3c2]/10 border border-[#3df3c2]/25 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#3df3c2]" />
      </div>
      <div>
        <div className="text-white text-2xl font-bold leading-none">{value}</div>
        <div className="text-white/50 text-xs mt-1">{label}</div>
      </div>
    </div>
  );
}

function AdminDashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0 });
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: leadsData }, { data: statsData }] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(leadsData);
      setStats(statsData);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  const removeLead = async (lead) => {
    const ok = window.confirm(`Remove ${lead.name} from your leads?`);
    if (!ok) return;
    // optimistic update
    const prev = leads;
    setLeads((curr) => curr.filter((l) => l.id !== lead.id));
    setStats((s) => ({ ...s, total: Math.max(0, s.total - 1) }));
    try {
      await axios.delete(`${API}/admin/leads/${lead.id}`, { headers });
      toast.success(`${lead.name} removed.`);
    } catch (err) {
      // revert
      setLeads(prev);
      setStats((s) => ({ ...s, total: s.total + 1 }));
      if (err?.response?.status === 401) {
        toast.error("Session expired. Sign in again.");
        onLogout();
      } else {
        toast.error("Failed to remove lead.");
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        l.phone.toLowerCase().includes(q)
    );
  }, [leads, query]);

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast.error("No leads to export.");
      return;
    }
    const header = ["Name", "Email", "Phone", "Created At"];
    const rows = filtered.map((l) => [
      l.name,
      l.email,
      l.phone,
      new Date(l.created_at).toISOString(),
    ]);
    const csv = [header, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const s = String(cell ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bks-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/5 px-6 md:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_bulk-supply-co/artifacts/6yi47sue_bks%20logo.png"
            alt="BKS"
            className="w-9 h-9 object-contain"
          />
          <div>
            <div className="text-white text-sm font-semibold tracking-wide">BKS WHOLESALE</div>
            <div className="text-white/45 text-[11px] tracking-[0.2em] uppercase">Admin</div>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center gap-2 text-white/65 hover:text-white text-sm transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Leads</h1>
            <p className="text-white/55 text-sm mt-1">All form submissions, newest first.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-white/10 hover:border-white/25 text-sm text-white/80 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={exportCsv}
              className="btn-mint inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard Icon={Users} label="Total leads" value={stats.total} />
          <StatCard Icon={CalendarClock} label="New today" value={stats.today} />
          <StatCard Icon={Search} label="Showing" value={filtered.length} />
        </div>

        <div className="mt-6 relative">
          <Search className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="input-dark w-full pl-10 pr-4 py-3 rounded-xl text-sm"
          />
        </div>

        <div className="mt-6 card-dark overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/45 text-[11px] tracking-[0.18em] uppercase border-b border-white/5">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Phone</th>
                  <th className="px-5 py-3 font-medium">Submitted</th>
                  <th className="px-5 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-white/50">
                      Loading leads...
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-white/50">
                      No leads yet. Submissions will appear here.
                    </td>
                  </tr>
                )}
                {!loading &&
                  filtered.map((l) => (
                    <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors group">
                      <td className="px-5 py-4 text-white">{l.name}</td>
                      <td className="px-5 py-4 text-white/80">
                        <a href={`mailto:${l.email}`} className="hover:text-[#3df3c2]">
                          {l.email}
                        </a>
                      </td>
                      <td className="px-5 py-4 text-white/80">
                        <a href={`tel:${l.phone}`} className="hover:text-[#3df3c2]">
                          {l.phone}
                        </a>
                      </td>
                      <td className="px-5 py-4 text-white/55">{formatDate(l.created_at)}</td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => removeLead(l)}
                          title="Remove lead"
                          aria-label={`Remove ${l.name}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-white/10 text-white/55 hover:text-white hover:border-red-400/50 hover:bg-red-500/10 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <AdminLogin onSuccess={setToken} />;
  return <AdminDashboard token={token} onLogout={handleLogout} />;
}
